import i18n, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * A minimal fallback resource object.
 * In a production environment, resources should be fetched from the database.
 * This serves as a fallback for local development or if the API fails.
 */
export const FALLBACK_RESOURCE = {
  en: {
    translation: {
      nextUp: 'Next Up',
      readTheStory: 'Read the story',
      swipeLeftForMore: 'Swipe left for more',
      interestTitle: 'Explore the world in galleries',
      interestspageSubTitle:
        'From top stories to stay informed, to beautiful photo galleries to inspire you.',
      ourLatestPicks: 'Our Latest Picks',
      seeMore: 'See more',
      popularGalleries: 'Popular Galleries',
      discoverTopicsThatInterestYou: 'Discover Topics That Interest You',
      more: 'More',
      thanksForSigningUp: 'Thanks for Signing Up!',
      moreInspiringPhotos:
        'More inspiring photos and stories from FotoScapes are coming to your inbox soon.',
      curatedCollectionsDeliveredtoYourInbox:
        'Curated Collections Delivered to Your Inbox',
      discoverMoreInspiringPhotos:
        'Discover more inspiring photos and stories from FotoScapes.',
      signUp: 'Sign Up',
      everydayLifestyle: 'Everyday lifestyle for curious people.',
      followUs: 'Follow Us',
      galleries: 'Galleries',
      sneakers: 'Sneakers',
      celebrityHomes: 'Celebrity Homes',
      games: 'Games',
      jokes: 'Jokes',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      managePrivacy: 'Manage Privacy',
      doNotSellMyPersonalInformation: 'Do Not Sell My Personal Information',
      aboutUs: 'About Us',
      contactUs: 'Contact Us',
      popularTopics: 'Popular Topics',
      home: 'Home',
      'fashion&Sneakers': 'Fashion & Sneakers',
      travelDestinations: 'Travel Destinations',
      close: 'Close',
      gameTitle: 'Playable Games',
      gameSubtitle:
        'Kick back and enjoy some of our favorite playable games.',
      stories: 'Stories',
      fotoSCAPESSExclusive: 'FotoSCAPES Exclusive Stories',
      diveDeep: 'Dive deep into our most noteworthy topics',
      youMayLike: 'You May Like',
      youMightLike: 'You Might Like',
      relatedStories: 'Related Stories',
      lifestyle: 'lifestyle',
      culture: 'culture',
      travel: 'travel',
      entertainment: 'entertainment',
      latest: 'latest',
      readyToLevelUp: 'Ready to level up?',
      signUpForOur:
        'Sign up for our newsletter and unlock exclusive, life-changing guides!',
      lifeHacks: 'Life Hacks',
      styleTips: 'Style Tips',
      travelGuides: 'Travel Guides',
      emailAddress: 'Email Address',
      enterYourEmail: 'Enter your email',
      submit: 'Submit',
      notNow: 'Not now',
      haveIdeasOn: 'Have ideas on how we can improve?',
      sendThemOurWay: 'Send them our way!',
      ourTeamIsDedicated: 'Our team is dedicated to improving your experience.',
      sendUsAMessage: 'Send us a message',
      letUsKnow: 'Let us know how we’re doing',
      yourOpinionMatters: 'Your opinion matters!',
      feedback: 'Feedback',
      recommendedForYou: 'Recommended For You.',
      // NOTE: Most translations have been removed from this static object.
      // They should be fetched from the database at runtime.
      // Keeping a few essential keys for fallback purposes.
      galleryError: 'Error',
      galleryCouldNotLoad: 'Could not load gallery.',
      gallerySomethingWentWrong: 'Oops! Something went wrong.',
      galleryLoading: 'Loading...',
      sponsoredByOutbrain: 'Sponsored by Outbrain',
      pleaseSelectEmailCategory: 'Please select at least one category',
      emailInvalid: 'Invalid or missing email address',
      emailSpam: 'We do not accept email addresses from these providers',
      anErrorHasOccurred: 'An Error Has Occurred',
      pleaseTryAgainLater: 'Please try again later.',
      weAppreciateYourFeedback: 'We Appreciate Your Feedback!',
      feedbackInvalid: 'Please provide feedback',
      yourDailyBriefing: 'Your Daily Briefing',
      // Carousel translations
      carouselLoading: 'Loading carousel items...',
      carouselError: 'Error: {{error}}. Click to retry.',
      carouselEmpty: 'No carousel items available. Click to retry.',
      carouselRetry: 'Click to retry',
      pageNotFound: 'Oops! Page Not Found',
      goToHomePage: 'Go to Home Page',
      playNow: 'Play Now',
      moreGames: "More Games",
    }
  },
};

export const PRIMARY_LANG_LIST = ['en', 'es', 'pt'];

export const SUPPORTED_LANGS = [
  ...PRIMARY_LANG_LIST,
  'de',
  'fr',
  'hi',
  'it',
  'tl',
];

const i18nConfig = {
  supportedLngs: SUPPORTED_LANGS,
  resources: FALLBACK_RESOURCE, // Use the minimal fallback by default
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // react already safes from xss
  },
  options: {

  }
};

/**
 * Initializes the global i18next singleton for client-side rendering.
 * It attempts to use preloaded resources if they are available on the window object.
 */
export const initClientLocale = async (lang: string) => {
  const preloadedResources = (window as any).__I18N_RESOURCES__;
  const language = lang.split('-')[0];

  const res = preloadedResources ? (() => {
    // @ts-expect-error language is a valid key but TypeScript can't infer it
    const targetFallback = FALLBACK_RESOURCE[language] || FALLBACK_RESOURCE.en;

    return {
      [language]: {
        ...targetFallback,
        ...preloadedResources,
      }
    };
  })() : FALLBACK_RESOURCE;

  await i18n.use(initReactI18next).init({
    ...i18nConfig,
    lng: language,
    resources: res,
  });
  return i18n
};

/**
 * Creates a new, isolated i18next instance for a given request.
 * This is the correct way to handle i18n in an SSR environment
 * to avoid concurrency issues.
 *
 * @param lang The language for this specific request.
 * @param resources The translation resources fetched from the database for this request.
 * @returns A promise that resolves with the configured i18next instance.
 */
export const createI18nInstance = async (lang: string, resources: any) => {
  const instance = i18n.createInstance();
  const lng = lang.split('-')[0];
  // @ts-expect-error lng is an actual key here.
  const targetFallback = FALLBACK_RESOURCE[lng] || FALLBACK_RESOURCE.en;

  await instance.use(initReactI18next).init({
    ...i18nConfig,
    lng: lng,
    resources: {
      ...FALLBACK_RESOURCE,
      [lng]: {
        ...targetFallback,
        ...resources,
      },
    },
  });
  return instance
};

/**
 * A server-safe translation helper.
 * It requires the request-specific i18n instance to be passed.
 * Inside React components, it is STRONGLY recommended to use the `useTranslation` hook instead.
 *
 * @param value The object with language-keyed translations.
 * @param i18nInstance The request-specific i18next instance. Defaults to the global for client-side use.
 */
export const getSSRTranslation = (
  value: Record<string, string>,
  i18nInstance: I18nInstance = i18n
) => {
  const lang = i18nInstance.language || i18nInstance.resolvedLanguage || 'en';
  return value[lang] || value.en || '';
};

// The old initLocale is deprecated in favor of the new dynamic approach.
// It can be removed or kept for legacy parts of the app if needed.
// export const initLocale = ...

export const getTranslation = (value: Record<string, string>) => {
  const lang = resolvedLanguage();
  return value[lang] || value.en || '';
};

export const resolvedLanguage = (langFallback?: string) => {
  return i18n.language || i18n.resolvedLanguage || langFallback || 'en';
}
